import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { BadgeCheck, FileDown, PenLine, Save, Stamp } from 'lucide-react';
import toast from 'react-hot-toast';
import FormSection from '../../components/Common/FormSection';
import AuthorizationSignModal from '../../components/Process/AuthorizationSignModal';
import { licenseService } from '../../services/api';
import { userFacingError } from '../../i18n/userFacingError';
import { useAuth } from '../../contexts/AuthContext';
import {
  authorizationStatus,
  canEditAuthorization,
  canSignAuthorization,
  conditionsFromText,
  pendingSigners,
} from '../../utils/authorization';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? [];
}

function formatWhen(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function openPdfBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Tipos de licença com modelo de autorização (AP, ASV). Tipo sem modelo responde 404 e fica de fora. */
async function loadTemplates() {
  const tipos = unwrap((await licenseService.getTiposLicenca({ limit: 200 })).data);
  const pairs = await Promise.all(
    tipos.map(async (tipo) => {
      try {
        return [tipo.id, await licenseService.getAuthorizationTemplate(tipo.id)];
      } catch (error) {
        if (error?.response?.status === 404) return null;
        throw error;
      }
    })
  );
  return Object.fromEntries(pairs.filter(Boolean));
}

function AuthorizationEditor({ row, onSaved }) {
  const [texto, setTexto] = useState(row.texto || '');
  const [validade, setValidade] = useState(row.validade_dias);
  const [condicoes, setCondicoes] = useState((row.condicionantes || []).join('\n'));

  useEffect(() => {
    setTexto(row.texto || '');
    setValidade(row.validade_dias);
    setCondicoes((row.condicionantes || []).join('\n'));
  }, [row]);

  const save = useMutation(
    () =>
      licenseService.updateAuthorization(row.id, {
        texto,
        validade_dias: Number(validade),
        condicionantes: conditionsFromText(condicoes),
      }),
    {
      onSuccess: () => {
        toast.success('Rascunho da autorização salvo.');
        onSaved();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  return (
    <div className="mt-3 grid gap-3">
      <label className="form-group">
        <span className="form-label">Texto</span>
        <textarea className="form-input min-h-[160px] text-sm" value={texto} onChange={(e) => setTexto(e.target.value)} />
      </label>
      <div className="grid gap-3 sm:grid-cols-[160px_1fr]">
        <label className="form-group">
          <span className="form-label">Validade (dias)</span>
          <input
            type="number"
            min={1}
            className="form-input"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
          />
        </label>
        <label className="form-group">
          <span className="form-label">Condicionantes (uma por linha)</span>
          <textarea className="form-input min-h-[110px] text-sm" value={condicoes} onChange={(e) => setCondicoes(e.target.value)} />
        </label>
      </div>
      <div>
        <button
          type="button"
          className="btn btn-outline inline-flex items-center gap-2"
          disabled={save.isLoading || texto.trim().length < 3}
          onClick={() => save.mutate()}
        >
          <Save className="h-4 w-4" />
          Salvar rascunho
        </button>
      </div>
    </div>
  );
}

export default function ProcessoAutorizacaoSection({ processoId }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tipoId, setTipoId] = useState('');
  const [texto, setTexto] = useState('');
  const [signing, setSigning] = useState(null);

  const { data: templates = {} } = useQuery(['authorization-templates'], loadTemplates, { staleTime: 60_000 });
  const { data: rowsRaw, isLoading } = useQuery(
    ['autorizacoes-processo', processoId],
    () => licenseService.listAuthorizations(processoId),
    { enabled: !!processoId }
  );
  const rows = unwrap(rowsRaw);
  const tipoOptions = useMemo(() => Object.entries(templates), [templates]);
  const invalidate = () => queryClient.invalidateQueries(['autorizacoes-processo', processoId]);

  const create = useMutation(
    () => licenseService.createAuthorization({ tipo_licenca_id: tipoId, processo_id: processoId, texto }),
    {
      onSuccess: () => {
        toast.success('Rascunho da autorização criado.');
        setTexto('');
        invalidate();
      },
      onError: (e) => toast.error(userFacingError(e, 'saveError')),
    }
  );

  const download = async (row) => {
    try {
      const { data: blob } = await licenseService.downloadAuthorizationPdf(row.id);
      openPdfBlob(blob, `${String(row.numero).replace('/', '-')}.pdf`);
    } catch (e) {
      toast.error(userFacingError(e, 'loadError'));
    }
  };

  if (!tipoOptions.length && !rows.length) return null;

  return (
    <FormSection title="Autorizações" icon={Stamp} variant="primary">
      <p className="mb-4 text-sm text-slate-600">
        Autorização de Poda, de Supressão e outros documentos com modelo do órgão. Cada assinante assina com o próprio
        login; a última assinatura numera e emite o documento.
      </p>

      {isLoading ? (
        <p className="text-sm text-slate-500">Carregando autorizações…</p>
      ) : (
        <ul className="mb-5 space-y-4">
          {rows.map((row) => {
            const template = templates[row.tipo_licenca_id];
            const status = authorizationStatus(row);
            const pending = pendingSigners(row, template);
            return (
              <li key={row.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold text-slate-900">{template?.titulo || 'Autorização'}</span>
                    {row.numero ? <span className="font-mono text-sm text-slate-700">{row.numero}</span> : null}
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        status.tone === 'success'
                          ? 'bg-emerald-50 text-emerald-800'
                          : status.tone === 'warning'
                            ? 'bg-amber-50 text-amber-800'
                            : 'bg-slate-100 text-slate-700'
                      }`}
                    >
                      {status.label}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canSignAuthorization(row, template, user?.id) ? (
                      <button
                        type="button"
                        className="btn btn-primary inline-flex items-center gap-2"
                        onClick={() => setSigning(row)}
                      >
                        <PenLine className="h-4 w-4" />
                        Assinar
                      </button>
                    ) : null}
                    {row.numero ? (
                      <button type="button" className="btn btn-outline inline-flex items-center gap-2" onClick={() => download(row)}>
                        <FileDown className="h-4 w-4" />
                        Baixar PDF
                      </button>
                    ) : null}
                  </div>
                </div>

                {(row.assinaturas || []).length ? (
                  <ul className="mt-3 space-y-1 text-sm text-slate-700">
                    {row.assinaturas.map((s) => (
                      <li key={s.user_id} className="flex items-center gap-2">
                        <BadgeCheck className="h-4 w-4 text-emerald-600" aria-hidden />
                        Assinado por {s.nome}, {s.cargo}, em {formatWhen(s.assinado_em)}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!row.numero && pending.length ? (
                  <p className="mt-2 text-xs text-slate-500">
                    Falta assinar: {pending.map((s) => s.nome).join(', ')}
                  </p>
                ) : null}
                {row.numero ? (
                  <p className="mt-2 text-xs text-slate-500">
                    Válida até {new Date(`${row.validade}T12:00:00`).toLocaleDateString('pt-BR')} · código{' '}
                    <span className="font-mono">{row.codigo_verificacao}</span>
                  </p>
                ) : null}

                {canEditAuthorization(row) ? <AuthorizationEditor row={row} onSaved={invalidate} /> : null}
              </li>
            );
          })}
        </ul>
      )}

      {rows.length === 0 && tipoOptions.length ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
          <h3 className="mb-3 text-sm font-semibold text-slate-900">Nova autorização</h3>
          <label className="form-group">
            <span className="form-label">Documento</span>
            <select className="form-input" value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
              <option value="">Selecione…</option>
              {tipoOptions.map(([id, template]) => (
                <option key={id} value={id}>
                  {template.titulo} ({template.sigla})
                </option>
              ))}
            </select>
          </label>
          <label className="form-group mt-3">
            <span className="form-label">Texto</span>
            <textarea
              className="form-input min-h-[160px] text-sm"
              placeholder="A Secretaria Municipal de Meio Ambiente…, vem informar que: Está autorizada a poda de…"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
            />
          </label>
          <p className="mt-1 text-xs text-slate-500">
            Validade e condicionantes vêm do modelo do documento e podem ser ajustadas antes da primeira assinatura.
          </p>
          <button
            type="button"
            className="btn btn-primary mt-3"
            disabled={!tipoId || texto.trim().length < 3 || create.isLoading}
            onClick={() => create.mutate()}
          >
            Criar rascunho
          </button>
        </div>
      ) : null}

      {signing ? (
        <AuthorizationSignModal
          authorization={signing}
          template={templates[signing.tipo_licenca_id]}
          onClose={() => setSigning(null)}
          onSigned={invalidate}
        />
      ) : null}
    </FormSection>
  );
}
